import { 
  Alert, 
  Button, 
  Collapse, 
  createTheme, 
  ThemeProvider, 
  IconButton, 
  Badge,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Fade,
  Zoom,
  Snackbar
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Edit, Delete, Visibility } from "@mui/icons-material";
import coursesService from "../../../services/CoursesService";
import teacherService from "../../../services/TeacherService";
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CommentIcon from '@mui/icons-material/Comment';
import PeopleIcon from '@mui/icons-material/People';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SchoolIcon from '@mui/icons-material/School';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import AssignCourseForm from "./AssignCourseForms/AssignCourseForm";

function ShowMyCoursesList({ show, enableEdit = false, onEditCourse, onDeleteCourse, onPreviewCourse, onDuplicateCourse, refreshTrigger }) {
  const [userCourses, setUserCourses] = useState([]);
  const [teacherNames, setTeacherNames] = useState({});
  /** id de curso → cantidad de solicitudes pendientes (solo para la badge del ícono de personas) */
  const [pendingByCourse, setPendingByCourse] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [duplicatingCourseId, setDuplicatingCourseId] = useState(null);
  const [alert, setAlert] = useState({ open: false, type: '', message: '' });
  const navigate = useNavigate();

  const fetchPendingCounts = async (courses) => {
    if (!courses?.length) {
      setPendingByCourse({});
      return;
    }
    try {
      const entries = await Promise.all(
        courses.map(async (c) => {
          try {
            const data = await coursesService.getCoursePendingRequests(c.id, navigate);
            const count = Array.isArray(data) ? data.length : 0;
            return [c.id, count];
          } catch {
            return [c.id, 0];
          }
        })
      );
      setPendingByCourse(Object.fromEntries(entries));
    } catch (e) {
      console.error('Error al cargar solicitudes pendientes por curso:', e);
    }
  };

  const cleanHtml = (htmlString) => {
    if (!htmlString) return '';
    return htmlString.replace(/<[^>]*>/g, '');
  };

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const usuarioConCedula = JSON.parse(localStorage.getItem("usuarioConCedula") || "{}");
      const teacherId = usuarioConCedula.id;
      
      if (!teacherId) {
        throw new Error('No se pudo obtener el ID del profesor');
      }
      
      const courses = await coursesService.getCoursesByTeacher(teacherId, navigate); 
      setUserCourses(courses);
      await fetchPendingCounts(courses);

      const uniqueTeacherIds = [
        ...new Set(
          (courses || [])
            .map((c) => c.teacherId ?? c.TeacherId)
            .filter((id) => id != null && Number(id) > 0)
            .map((id) => Number(id))
        ),
      ];
      const namesMap = {};
      await Promise.all(
        uniqueTeacherIds.map(async (id) => {
          const courseMatch = (courses || []).find(
            (c) => Number(c.teacherId ?? c.TeacherId) === id
          );
          const embedded = courseMatch?.teacherName || courseMatch?.TeacherName;
          if (embedded) {
            namesMap[id] = embedded;
            return;
          }
          try {
            const teacherData = await teacherService.getTeacher(id);
            namesMap[id] = teacherData.name || teacherData.nombre || teacherData.Name || '';
          } catch {
            if (Number(usuarioConCedula.id) === id) {
              namesMap[id] = usuarioConCedula.name || usuarioConCedula.nombre || '';
            }
          }
        })
      );
      setTeacherNames(namesMap);
    } catch (error) {
      console.error("Error fetching courses:", error);
      setAlert({
        open: true,
        type: 'error',
        message: 'Error al cargar los cursos'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchCourses();
    }
  }, [show, navigate]);

  // Refresh courses when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchCourses();
    }
  }, [refreshTrigger]);

  const handleViewCourse = async (course) => {
    if (onPreviewCourse) {
      // If preview handler is provided, fetch full course data and show preview
      try {
        const fullCourseData = await coursesService.getCourseById(course.id, navigate);
        onPreviewCourse(fullCourseData);
      } catch (error) {
        console.error("Error loading course for preview:", error);
        setAlert({
          open: true,
          type: 'error',
          message: 'Error al cargar el curso para vista previa'
        });
      }
    } else {
      // Fall back to navigation if no preview handler
      navigate(`/curso/${course.id}`);
    }
  };

  const handleStudentsClick = (courseId, course) => {
    setSelectedCourseId(courseId);
    setSelectedCourse(course);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    const closedCourseId = selectedCourseId;
    setOpenDialog(false);
    setSelectedCourseId(null);
    setSelectedCourse(null);
    if (closedCourseId) {
      coursesService
        .getCoursePendingRequests(closedCourseId, navigate)
        .then((data) => {
          const count = Array.isArray(data) ? data.length : 0;
          setPendingByCourse((prev) => ({ ...prev, [closedCourseId]: count }));
        })
        .catch(() => {});
    }
   };
  
  const handleEditCourse = async (course) => {
    try {
      // Fetch complete course data for editing
      const fullCourseData = await coursesService.getCourseById(course.id, navigate);
      onEditCourse(fullCourseData);
    } catch (error) {
      console.error("Error loading course for editing:", error);
      setAlert({
        open: true,
        type: 'error',
        message: 'Error al cargar el curso para editar'
      });
    }
  };

  const handleDeleteCourse = (course) => {
    onDeleteCourse(course);
    
  };

  const handleDuplicateCourse = async (course) => {
    if (!onDuplicateCourse || !course?.id) return;
    setDuplicatingCourseId(course.id);
    try {
      await onDuplicateCourse(course);
    } catch (error) {
      console.error('Error duplicating course:', error);
      setAlert({
        open: true,
        type: 'error',
        message: error.message || 'Error al duplicar el curso',
      });
    } finally {
      setDuplicatingCourseId(null);
    }
  };

  const theme = createTheme({
    palette: {
      greenAqua: {
        main: "#4DABA4",
        light: "#7BCDC7",
        dark: "#2E8A82",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#FF6B6B",
        light: "#FF8E8E",
        dark: "#E55555",
      },
      info: {
        main: "#4ECDC4",
        light: "#7EDDD6",
        dark: "#2EB8B0",
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            '&:hover': {
              boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
          },
        },
      },
    },
  });

  if (loading) {
    return (
      <Collapse in={show} sx={{ mt: 3, width: "100%" }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Typography>Cargando cursos...</Typography>
        </Box>
      </Collapse>
    );
  }

  return (
    <>
      <Collapse in={show} sx={{ mt: 3, width: "100%" }}>
      <Box sx={{ 
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          borderRadius: '20px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 3, 
              textAlign: 'center',
              background: 'linear-gradient(45deg, #4DABA4 30%, #7BCDC7 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold'
            }}
          >
            Mis Cursos
          </Typography>
          
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {userCourses.length > 0 ? (
              userCourses.map((course, index) => {
                // Debug: Log course data to see what fields are available
                if (index === 0) {
                  console.log('Course data sample:', course);
                  console.log('Available thumbnail fields:', {
                    thumbnailPath: course.thumbnailPath,
                    thumbnailUrl: course.thumbnailUrl,
                    thumbnail: course.thumbnail,
                    Thumbnail: course.Thumbnail
                  });
                }
                
                // Try multiple possible field names for thumbnail
                const imagePath = course.thumbnailPath || course.thumbnailUrl || course.thumbnail || course.Thumbnail;
                let thumbnail = "/img/default.png";
                
                if (imagePath) {
                  // Handle cases where imagePath might already start with '/' or be a full URL
                  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
                    thumbnail = imagePath;
                  } else {
                    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
                    thumbnail = `${process.env.REACT_APP_LOCATION}${cleanPath}`;
                  }
                }

                const pendingCount = pendingByCourse[course.id] ?? 0;
                
                return (
                <Fade in={true} timeout={300 + index * 100} key={course.id}>
                  <div
                    className="flex overflow-hidden relative flex-col w-full h-full bg-white rounded-2xl shadow-xl transition-all duration-500 transform group hover:shadow-2xl hover:-translate-y-2"
                    style={{ 
                      minWidth: '280px',
                      background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                      border: '1px solid rgba(77, 171, 164, 0.1)'
                    }}
                  >
                    {/* Badges de estado */}
                    <div className="absolute top-3 left-3 z-10 flex flex-col gap-1">
                      <Box
                        sx={{
                          background: 'linear-gradient(45deg, #4DABA4 30%, #7BCDC7 90%)',
                          color: 'white',
                          px: 2,
                          py: 0.5,
                          borderRadius: '20px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          boxShadow: '0 2px 8px rgba(77, 171, 164, 0.3)',
                          width: 'fit-content'
                        }}
                      >
                        Activo
                      </Box>
                      {course.isPublic && (
                        <Box
                          sx={{
                            background: 'linear-gradient(45deg, #7C6BC4 30%, #9B8CD9 90%)',
                            color: 'white',
                            px: 2,
                            py: 0.5,
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 8px rgba(124, 107, 196, 0.3)',
                            width: 'fit-content'
                          }}
                        >
                          Público
                        </Box>
                      )}
                      {(course.isWelcome || course.IsWelcome) && (
                        <Box
                          sx={{
                            background: 'linear-gradient(45deg, #F59E0B 30%, #FBBF24 90%)',
                            color: 'white',
                            px: 2,
                            py: 0.5,
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 8px rgba(245, 158, 11, 0.35)',
                            width: 'fit-content'
                          }}
                        >
                          Bienvenida
                        </Box>
                      )}
                    </div>

                    <div className="flex overflow-hidden relative justify-center items-center bg-gray-200 aspect-video">
                      <img
                        alt={cleanHtml(course.title)}
                        src={thumbnail}
                        className="object-contain max-w-full max-h-full w-full h-full transition duration-700 ease-out group-hover:scale-105"
                        onError={(e) => {
                          e.target.src = '/img/default.png';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t to-transparent opacity-0 transition-opacity duration-300 from-black/20 group-hover:opacity-100" />
                    </div>
                    
                    <div className="flex-grow p-6 space-y-4">
                      <Typography 
                        variant="h6" 
                        className="font-bold leading-snug text-gray-800 transition-colors duration-300 group-hover:text-greenAqua"
                        sx={{ 
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {cleanHtml(course.title)}
                      </Typography>
                      
                      <div className="space-y-3">
                        <div className="flex items-center text-sm text-gray-600">
                          <AccessTimeIcon sx={{ fontSize: 16, mr: 1, color: '#4DABA4' }} />
                          <span>{new Date(course.createdAt).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}</span>
                        </div>
                        
                        <div className="flex items-center text-sm text-gray-600">
                          <SchoolIcon sx={{ fontSize: 16, mr: 1, color: '#4DABA4' }} />
                          <span>{course.duration || '1h 30m'}</span>
                        </div>
                        
                        <div className="flex items-center text-sm text-gray-600">
                          <PersonIcon sx={{ fontSize: 16, mr: 1, color: '#4DABA4' }} />
                          <span>
                            {course.teacherName ||
                              teacherNames[course.teacherId] ||
                              teacherNames[Number(course.teacherId)] ||
                              'Sin asignar'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-gradient-to-r from-gray-50 to-white border-t border-gray-100">
                      <div className="flex justify-around mb-4">
                        {enableEdit && (
                          <Tooltip title="Editar curso" arrow>
                            <IconButton 
                              size="medium" 
                              onClick={() => handleEditCourse(course)}
                              sx={{ 
                                color: '#4DABA4',
                                '&:hover': { 
                                  background: 'rgba(77, 171, 164, 0.1)',
                                  color: '#2E8A82'
                                }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {enableEdit && onDuplicateCourse && (
                          <Tooltip title="Duplicar curso" arrow>
                            <span>
                              <IconButton
                                size="medium"
                                onClick={() => handleDuplicateCourse(course)}
                                disabled={duplicatingCourseId === course.id}
                                sx={{
                                  color: '#7C6BC4',
                                  '&:hover': {
                                    background: 'rgba(124, 107, 196, 0.1)',
                                    color: '#5E4DA8'
                                  }
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        
                        <Tooltip title="Ver comentarios" arrow>
                          <IconButton 
                            size="medium" 
                            sx={{ 
                              color: '#FF6B6B',
                              '&:hover': { 
                                background: 'rgba(255, 107, 107, 0.1)',
                                color: '#E55555'
                              }
                            }}
                          >
                            <CommentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip
                          title={
                            pendingCount > 0
                              ? `${pendingCount} solicitud${pendingCount === 1 ? '' : 'es'} pendiente${pendingCount === 1 ? '' : 's'}`
                              : 'Gestionar estudiantes / solicitudes'
                          }
                          arrow
                        >
                          <Badge
                            invisible={pendingCount === 0}
                            badgeContent={pendingCount > 99 ? '99+' : pendingCount}
                            color="error"
                            sx={{
                              '& .MuiBadge-badge': {
                                fontWeight: 700,
                                boxShadow: '0 0 0 2px white'
                              }
                            }}
                          >
                            <IconButton 
                              size="medium" 
                              onClick={() => handleStudentsClick(course.id, course)}
                              sx={{ 
                                color: '#4ECDC4',
                                '&:hover': { 
                                  background: 'rgba(78, 205, 196, 0.1)',
                                  color: '#2EB8B0'
                                }
                              }}
                            >
                              <PeopleIcon fontSize="small" />
                            </IconButton>
                          </Badge>
                        </Tooltip>
                        
                        {enableEdit && (
                          <Tooltip title="Eliminar curso" arrow>
                            <IconButton 
                              size="medium" 
                              onClick={() => handleDeleteCourse(course)}
                              sx={{ 
                                color: '#f44336',
                                '&:hover': { 
                                  background: 'rgba(244, 67, 54, 0.1)',
                                  color: '#d32f2f'
                                }
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                      
                      <ThemeProvider theme={theme}>
                        <Button
                          variant="contained"
                          color="greenAqua"
                          fullWidth
                          size="medium"
                          onClick={() => handleViewCourse(course)}
                          startIcon={<VisibilityIcon />}
                          sx={{ 
                            py: 1.5,
                            fontSize: '0.9rem',
                            fontWeight: 'bold',
                            background: 'linear-gradient(45deg, #4DABA4 30%, #7BCDC7 90%)',
                            '&:hover': {
                              background: 'linear-gradient(45deg, #2E8A82 30%, #4DABA4 90%)',
                            }
                          }}
                        >
                          Ver Curso
                        </Button>
                      </ThemeProvider>
                    </div>
                  </div>
                </Fade>
                );
              })
            ) : (
              <Zoom in={true} timeout={500}>
                <div className="flex flex-col col-span-full justify-center items-center p-12 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl border-2 border-blue-200 border-dashed">
                  <SchoolIcon sx={{ fontSize: 80, color: '#4DABA4', mb: 2 }} />
                  <Typography variant="h6" color="textSecondary" sx={{ mb: 2, textAlign: 'center' }}>
                    No tienes cursos creados
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
                    Comienza creando tu primer curso para compartir conocimiento
                  </Typography>
                </div>
              </Zoom>
            )}
          </div>
        </Box>
      </Collapse>

      {/* Diálogo mejorado para estudiantes inscritos */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #4DABA4 30%, #7BCDC7 90%)',
          color: 'white',
          borderRadius: '20px 20px 0 0',
          padding: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <PeopleIcon />
          <Typography variant="h6" sx={{ fontWeight: 'bold', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            {selectedCourse ? `Gestionar estudiantes — ${cleanHtml(selectedCourse.title)}` : 'Gestionar estudiantes inscritos'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px' }}>
          {selectedCourseId && (
            <AssignCourseForm 
              courseId={selectedCourseId} 
              onClose={handleCloseDialog}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ShowMyCoursesList;